import { useQuery } from '@tanstack/react-query';
import { ConfigProvider, Select } from 'antd';
import enUS from 'antd/lib/locale/en_US';
import frCA from 'antd/lib/locale/fr_CA';
import { DateTime } from 'luxon';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { Col, Row } from 'react-bootstrap';
import { useHistory, useLocation, withRouter } from 'react-router-dom';
import { cgmApi, mealApi } from '../api';
import { useStore } from '../context';
import { Trans, useTranslation } from '../i18n';
import { FilterType, RangePickerWithActivityIndicators } from './foodFilterNew';
import { useCalendarActivityIndicators } from './useCalendarActivityFeed';
import 'react-day-picker/lib/style.css';
import './foodFilter.scss';

export const DateRangeDropdown = ({
  setDateRangeFilter,
}: {
  setDateRangeFilter: (filter: any) => void,
  style?: React.CSSProperties,
}) => {
  const { patient, allReports } = useStore();
  const { t } = useTranslation();
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({
    'Report Dates': true,
    'CGM Devices': true,
  });

  // Fetch CGM device data
  const { data: cgmDevices } = useQuery({
    queryKey: ['cgm-devices', patient?.patient_id],
    queryFn: async () => {
      if (!patient?.patient_id) {
        return null;
      }
      const response = await cgmApi.appApiCgmApiGetPatientCgmDevicesTable({
        patient_id: patient.patient_id,
      });
      return response.data;
    },
    enabled: !!patient?.patient_id,
  });

  const handlePresetChange = (value: string) => {
    // Handle both CGM devices and report dates
    // eslint-disable-next-line i18next/no-literal-string
    if (value.startsWith('report_')) {
      const reportId = value.replace('report_', '');
      const report = allReports?.find(r => r.patient_report_id.toString() === reportId);
      if (!report) {
        return;
      }

      const startDate = moment(report.date_since).startOf('day');
      const endDate = moment(report.date_until).endOf('day');

      setDateRangeFilter(prev => ({
        ...prev,
        rangePickerValue: [startDate, endDate],
      }));
    } else {
      if (!cgmDevices?.rows) {
        return;
      }

      const device = cgmDevices.rows.find(d => d.device_id === value);
      if (!device) {
        return;
      }

      const startDate = moment(device.first_reading).startOf('day');
      const endDate = moment(device.last_reading).endOf('day');

      setDateRangeFilter(prev => ({
        ...prev,
        rangePickerValue: [startDate, endDate],
      }));
    }
  };

  const toggleGroup = (groupName: string) => {
    setExpandedGroups(prev => ({
      ...prev,
      [groupName]: !prev[groupName],
    }));
  };

  // Sort reports by date (most recent first)
  const sortedReports = allReports
    ? [...allReports].sort((a, b) => moment(b.date_until).valueOf() - moment(a.date_until).valueOf())
    : [];

  const hasCgmDevices = cgmDevices?.rows && cgmDevices.rows.length > 0;
  const hasReports = sortedReports && sortedReports.length > 0;

  // Only render if we have either reports or CGM devices
  if (!hasCgmDevices && !hasReports) {
    return null;
  }

  return (
    <Select
      style={{
        width: 275,
        marginLeft: 8,
        fontSize: '14px',
        textTransform: 'none',
        letterSpacing: 'normal',
      }}
      placeholder={t('Quick select date range')}
      onChange={handlePresetChange}
      listHeight={250}
      popupClassName="date-range-dropdown"
      dropdownRender={menu => (
        <div>
          {menu}
          <style jsx global>
            {`
            .date-range-dropdown .rc-virtual-list-holder {
              max-height: 250px;
              overflow-y: auto !important;
            }

            .date-range-dropdown .rc-virtual-list::-webkit-scrollbar {
              width: 8px;
            }

            .date-range-dropdown .rc-virtual-list::-webkit-scrollbar-track {
              background: #f0f0f0;
              border-radius: 4px;
            }

            .date-range-dropdown .rc-virtual-list::-webkit-scrollbar-thumb {
              background: #bfbfbf;
              border-radius: 4px;
            }

            .date-range-dropdown .rc-virtual-list::-webkit-scrollbar-thumb:hover {
              background: #a6a6a6;
            }

            .date-range-dropdown .rc-virtual-list {
              scrollbar-width: thin;
              scrollbar-color: #bfbfbf #f0f0f0;
            }

            .ant-select-item-group {
              cursor: pointer;
              padding: 8px 12px;
              font-weight: 500;
              background-color: #f5f5f5;
              border-bottom: 1px solid #e8e8e8;
            }

            .ant-select-item-group:hover {
              background-color: #e6f7ff;
            }

            .group-arrow {
              display: inline-block;
              margin-left: 8px;
              width: 0;
              height: 0;
              border-left: 5px solid transparent;
              border-right: 5px solid transparent;
              border-top: 5px solid #666;
              transition: transform 0.2s;
            }
          `}
          </style>
        </div>
      )}
    >
      {/* Only show Reports section if we have reports */}
      {hasReports && (
        <Select.OptGroup
          label={
            <div onClick={() => toggleGroup('Report Dates')}>
              {t('Report Dates')}{' '}
              <span
                className="group-arrow"
                style={{
                  transform: expandedGroups['Report Dates'] ? 'rotate(180deg)' : 'none',
                }}
              />
            </div>
          }
        >
          {expandedGroups['Report Dates'] && sortedReports.map(report => (
            <Select.Option
              key={`report_${report.patient_report_id}`}
              value={`report_${report.patient_report_id}`}
            >
              {/* eslint-disable-next-line i18next/no-literal-string */}
              {`RxFood Report (${moment(report.date_since).format('MMM D, YYYY')} - ${
                moment(report.date_until).format('MMM D, YYYY')
              })`}
            </Select.Option>
          ))}
        </Select.OptGroup>
      )}

      {/* Only show CGM section if we have devices */}
      {hasCgmDevices && (
        <Select.OptGroup
          label={
            <div onClick={() => toggleGroup('CGM Devices')}>
              {t('CGM Devices')}{' '}
              <span
                className="group-arrow"
                style={{
                  transform: expandedGroups['CGM Devices'] ? 'rotate(180deg)' : 'none',
                }}
              />
            </div>
          }
        >
          {expandedGroups['CGM Devices'] && cgmDevices.rows
            .sort((a, b) => moment(b.last_reading).valueOf() - moment(a.last_reading).valueOf())
            .map(device => (
              <Select.Option key={device.device_id} value={device.device_id}>
                {`${device.device} (${moment(device.first_reading).format('MMM D, YYYY')} - ${
                  moment(device.last_reading).format('MMM D, YYYY')
                })`}
              </Select.Option>
            ))}
        </Select.OptGroup>
      )}
    </Select>
  );
};

export const useDateRange = () => {
  const { patient } = useStore();
  const history = useHistory();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const startQuery = searchParams.get('start');
  const endQuery = searchParams.get('end');
  const [hasUpdatedDates, setHasUpdatedDates] = useState(false);

  // Initialize with default values
  const [dateRangeFilter, setDateRangeFilter] = useState<FilterType>(() => {
    // If we have URL parameters, use those
    if (startQuery && endQuery) {
      return {
        rangePickerValue: [
          moment(startQuery, 'YYYY-MM-DD'),
          moment(endQuery, 'YYYY-MM-DD'),
        ],
        dateRanges: [],
      };
    } else if (startQuery && !endQuery) {
      return {
        rangePickerValue: [
          moment(startQuery, 'YYYY-MM-DD'),
          moment(startQuery, 'YYYY-MM-DD').add(1, 'month').endOf('day'),
        ],
        dateRanges: [],
      };
    } else if (!startQuery && endQuery) {
      return {
        rangePickerValue: [
          moment(endQuery, 'YYYY-MM-DD').subtract(1, 'month').startOf('day'),
          moment(endQuery, 'YYYY-MM-DD'),
        ],
        dateRanges: [],
      };
    }
    return {
      rangePickerValue: [
        moment().subtract(1, 'month').startOf('day'),
        moment().endOf('day'),
      ],
      dateRanges: [],
    };
  });

  // Check if we have URL parameters
  const hasUrlParams = startQuery || endQuery;

  // Fetch most recent meal date if no dates are provided
  const { data: meals, isLoading } = useQuery({
    queryKey: ['recent-meals', patient?.patient_id],
    queryFn: async () => {
      if (!patient?.patient_id) {
        return [];
      }
      const res = await mealApi.appApiMealGetMeals({
        patient_id: patient.patient_id,
        date_since: moment().subtract(365, 'days').format('YYYY-MM-DD'),
        date_until: moment().format('YYYY-MM-DD'),
      });
      return res.data;
    },
    enabled: !hasUrlParams && !hasUpdatedDates && !!patient?.patient_id,
  });

  // Update date range when meals are loaded
  useEffect(() => {
    if (hasUrlParams) {
      setHasUpdatedDates(true);
      return;
    }

    if (isLoading || hasUpdatedDates) {
      return;
    }

    if (!meals?.length) {
      setHasUpdatedDates(true);
      return;
    }

    const mostRecentMeal = meals[meals.length - 1];
    const newEndDate = moment(mostRecentMeal.meal_date).endOf('day');
    const newStartDate = newEndDate.clone().subtract(1, 'month').startOf('day');

    setDateRangeFilter(prev => ({
      ...prev,
      rangePickerValue: [newStartDate, newEndDate],
    }));
    setHasUpdatedDates(true);

    // Update URL after setting the new date range
    const params = new URLSearchParams(location.search);
    // eslint-disable-next-line i18next/no-literal-string
    params.set('start', newStartDate.format('YYYY-MM-DD'));
    // eslint-disable-next-line i18next/no-literal-string
    params.set('end', newEndDate.format('YYYY-MM-DD'));
    history.replace({
      pathname: location.pathname,
      search: params.toString(),
    });
  }, [meals, isLoading, hasUpdatedDates, hasUrlParams, history, location.pathname, location.search]);

  useEffect(() => {
    if (hasUpdatedDates) {
      const [start, end] = dateRangeFilter.rangePickerValue;
      const params = new URLSearchParams(location.search);
      // eslint-disable-next-line i18next/no-literal-string
      params.set('start', start.format('YYYY-MM-DD'));
      // eslint-disable-next-line i18next/no-literal-string
      params.set('end', end.format('YYYY-MM-DD'));
      history.replace({
        pathname: location.pathname,
        search: params.toString(),
      });
    }
  }, [dateRangeFilter, history, location.pathname, location.search, hasUpdatedDates]);

  // Validate dates
  const [startDate, endDate] = dateRangeFilter.rangePickerValue;
  useEffect(() => {
    if (startDate.isAfter(endDate)) {
      setDateRangeFilter(prev => ({
        ...prev,
        rangePickerValue: [endDate, startDate],
      }));
    } else if (
      startDate.isAfter(moment().add(1, 'day').endOf('day'))
      || endDate.isAfter(moment().add(1, 'day').endOf('day'))
    ) {
      setDateRangeFilter(prev => ({
        ...prev,
        rangePickerValue: [
          moment().subtract(1, 'month').add(1, 'day').startOf('day'),
          moment().add(1, 'day').endOf('day'),
        ],
      }));
    }
  }, [startDate, endDate, setDateRangeFilter]);

  return {
    dateRangeFilter,
    setDateRangeFilter,
    hasUpdatedDates,
  };
};

export const withFilterPanel = (
  WrappedComponent: React.ComponentType<{
    since: moment.Moment,
    until: moment.Moment,
    hasUpdatedDates: boolean,
  }>,
) => {
  return withRouter(function(props) {
    const { i18n } = useTranslation();

    const { dateRangeFilter, setDateRangeFilter, hasUpdatedDates } = useDateRange();

    const [dates, setDates] = useState({
      oldestDate: DateTime.local().minus({ days: 65 }),
      newestDate: null,
    });
    const { calendarActivityIndicators, isFetching } = useCalendarActivityIndicators(dates);

    return (
      <>
        <Row className="logFilterPanel">
          <Col className="logFilterCol">
            <span className="mr-5">
              <Trans>filter by date</Trans>
            </span>
            <ConfigProvider
              locale={i18n.language === 'fr' ? frCA : enUS}
              theme={{
                components: {
                  DatePicker: {
                    cellHeight: 32,
                    cellWidth: 32,
                  },
                },
              }}
            >
              <RangePickerWithActivityIndicators
                calendarActivityIndicators={calendarActivityIndicators}
                isFetching={isFetching}
                filter={dateRangeFilter}
                dates={dates}
                setFilter={setDateRangeFilter}
                setDates={setDates}
              />
              <DateRangeDropdown setDateRangeFilter={setDateRangeFilter} />
            </ConfigProvider>
          </Col>
        </Row>

        <WrappedComponent
          since={dateRangeFilter.rangePickerValue[0]}
          until={dateRangeFilter.rangePickerValue[1]}
          hasUpdatedDates={hasUpdatedDates}
        />
      </>
    );
  });
};
